import {
  TEAM_BLUEPRINT_CONNECTIONS,
  TEAM_BLUEPRINT_LIMITS,
  type CadenceKind,
  type CadenceMeta,
  type ResearchConfidence,
  type WorkspaceTeamBlueprint,
} from "@yagni/shared";

export type CompanyProfileDraft = {
  legalOrBrandName: string;
  oneLiner: string;
  whatTheyDo: string;
  whoTheyServe: string;
  industry: string;
  stageOrSize: string;
  businessModel: string;
  products: string;
  customerSegments: string;
  operatingPriorities: string;
};

export type TeamDraft = {
  id: string;
  enabled: boolean;
  teamName: string;
  responsibilities: string;
  numberLabel: string;
  expectedConnectorIds: string[];
  cadenceKind: CadenceKind;
  cadenceMeta: CadenceMeta;
  playbookRules: string;
};

export type BlueprintDraft = {
  companyProfile: CompanyProfileDraft;
  teams: TeamDraft[];
  primaryTeamId: string;
};

export type RequestPhase = "idle" | "saving" | "installing" | "deferring" | "reloading";

const LIMITS = TEAM_BLUEPRINT_LIMITS;

export const MAX_ENABLED_TEAMS = LIMITS.maxEnabledTeams;

export function teamBlueprintDraftStorageKey(blueprintId: string): string {
  return `yagni:team-blueprint-draft:${blueprintId}`;
}

export const CONNECTION_OPTIONS = TEAM_BLUEPRINT_CONNECTIONS
  .map((entry) => ({ ...entry }))
  .sort((left, right) => left.label.localeCompare(right.label));

const CONNECTION_BY_ID: ReadonlyMap<string, (typeof CONNECTION_OPTIONS)[number]> = new Map(
  CONNECTION_OPTIONS.map((entry) => [entry.id, entry]),
);

export function lines(value: string): string[] {
  return [...new Set(value.split("\n").map((line) => line.trim()).filter(Boolean))];
}

function optional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function draftFromBlueprint(blueprint: WorkspaceTeamBlueprint): BlueprintDraft {
  const serverEnabled = blueprint.teams.filter((team) => team.enabled).map((team) => team.id);
  const fallbackEnabled = blueprint.teams
    .filter((team) => team.recommended)
    .slice(0, 3)
    .map((team) => team.id);
  const enabledIds = new Set(serverEnabled.length > 0 ? serverEnabled : fallbackEnabled);
  const firstEnabled = blueprint.teams.find((team) => enabledIds.has(team.id))?.id ?? blueprint.teams[0]?.id ?? "";
  const primaryTeamId = enabledIds.has(blueprint.primaryTeamId)
    ? blueprint.primaryTeamId
    : firstEnabled;

  return {
    companyProfile: {
      legalOrBrandName: blueprint.companyProfile.legalOrBrandName,
      oneLiner: blueprint.companyProfile.oneLiner,
      whatTheyDo: blueprint.companyProfile.whatTheyDo,
      whoTheyServe: blueprint.companyProfile.whoTheyServe,
      industry: blueprint.companyProfile.industry,
      stageOrSize: blueprint.companyProfile.stageOrSize ?? "",
      businessModel: blueprint.companyProfile.businessModel ?? "",
      products: blueprint.companyProfile.products.join("\n"),
      customerSegments: blueprint.companyProfile.customerSegments.join("\n"),
      operatingPriorities: blueprint.companyProfile.operatingPriorities.join("\n"),
    },
    teams: blueprint.teams.map((team) => ({
      id: team.id,
      enabled: enabledIds.has(team.id),
      teamName: team.recipe.teamName,
      responsibilities: team.recipe.responsibilities.join("\n"),
      numberLabel: team.recipe.number?.label ?? "",
      expectedConnectorIds: [...team.expectedConnectorIds],
      cadenceKind: team.cadenceKind,
      cadenceMeta: { ...team.cadenceMeta },
      playbookRules: team.recipe.playbookRules.join("\n"),
    })),
    primaryTeamId,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Restore only a same-revision, structurally valid local review draft. */
export function restoreStoredDraft(
  blueprint: WorkspaceTeamBlueprint,
  raw: string | null,
): BlueprintDraft | null {
  if (!raw) return null;
  try {
    const envelope = JSON.parse(raw) as unknown;
    if (!isRecord(envelope) || envelope.revision !== blueprint.revision || !isRecord(envelope.draft)) {
      return null;
    }
    const candidate = envelope.draft;
    const companyProfile = candidate.companyProfile;
    if (!isRecord(companyProfile) || !Array.isArray(candidate.teams)) return null;
    const companyKeys: Array<keyof CompanyProfileDraft> = [
      "legalOrBrandName",
      "oneLiner",
      "whatTheyDo",
      "whoTheyServe",
      "industry",
      "stageOrSize",
      "businessModel",
      "products",
      "customerSegments",
      "operatingPriorities",
    ];
    if (companyKeys.some((key) => typeof companyProfile[key] !== "string")) return null;
    const expectedIds = [...blueprint.teams.map((team) => team.id)].sort();
    const teams = candidate.teams;
    if (teams.length !== expectedIds.length) return null;
    const validCadences = new Set<CadenceKind>(["editions", "daily", "weekly", "as_it_happens"]);
    for (const team of teams) {
      if (
        !isRecord(team) ||
        typeof team.id !== "string" ||
        typeof team.enabled !== "boolean" ||
        typeof team.teamName !== "string" ||
        typeof team.responsibilities !== "string" ||
        typeof team.numberLabel !== "string" ||
        !Array.isArray(team.expectedConnectorIds) ||
        team.expectedConnectorIds.some((id) => typeof id !== "string") ||
        !validCadences.has(team.cadenceKind as CadenceKind) ||
        !isRecord(team.cadenceMeta) ||
        typeof team.playbookRules !== "string"
      ) return null;
    }
    const restoredIds = teams.map((team) => String((team as Record<string, unknown>).id)).sort();
    if (restoredIds.some((id, index) => id !== expectedIds[index])) return null;
    if (typeof candidate.primaryTeamId !== "string") return null;
    return candidate as BlueprintDraft;
  } catch {
    return null;
  }
}

function companyProfilePayload(draft: CompanyProfileDraft) {
  return {
    legalOrBrandName: draft.legalOrBrandName.trim(),
    oneLiner: draft.oneLiner.trim(),
    whatTheyDo: draft.whatTheyDo.trim(),
    whoTheyServe: draft.whoTheyServe.trim(),
    industry: draft.industry.trim(),
    stageOrSize: optional(draft.stageOrSize),
    businessModel: optional(draft.businessModel),
    products: lines(draft.products),
    customerSegments: lines(draft.customerSegments),
    operatingPriorities: lines(draft.operatingPriorities),
  };
}

export function requestPayload(blueprint: WorkspaceTeamBlueprint, draft: BlueprintDraft) {
  // The primary Team is no longer a user decision (it only anchors the
  // blueprint's single-Team fallback and post-install Brief), so the payload
  // self-heals instead of surfacing a validation error: when the tracked
  // primary is disabled, the first enabled Team silently takes over.
  const enabled = draft.teams.filter((team) => team.enabled);
  const primaryTeamId = enabled.some((team) => team.id === draft.primaryTeamId)
    ? draft.primaryTeamId
    : enabled[0]?.id ?? draft.primaryTeamId;
  return {
    expectedRevision: blueprint.revision,
    primaryTeamId,
    companyProfile: companyProfilePayload(draft.companyProfile),
    teams: draft.teams.map((team) => ({
      id: team.id,
      enabled: team.enabled,
      teamName: team.teamName.trim(),
      responsibilities: lines(team.responsibilities),
      numberLabel: null,
      expectedConnectorIds: [...new Set(team.expectedConnectorIds)],
      cadenceKind: team.cadenceKind,
      cadenceMeta: team.cadenceMeta,
      playbookRules: lines(team.playbookRules),
    })),
  };
}

export function validateDraft(draft: BlueprintDraft): string | null {
  const company = companyProfilePayload(draft.companyProfile);
  if (!company.legalOrBrandName) {
    return "Add the company name before creating your Teams.";
  }
  if (
    company.products.length > LIMITS.maxProducts ||
    company.customerSegments.length > LIMITS.maxCustomerSegments
  ) {
    return `Keep products and customer segments to ${LIMITS.maxProducts} lines each.`;
  }
  if (company.operatingPriorities.length > LIMITS.maxOperatingPriorities) {
    return `Keep operating priorities to ${LIMITS.maxOperatingPriorities} lines.`;
  }
  if (
    company.products.some((item) => item.length > LIMITS.product) ||
    company.customerSegments.some((item) => item.length > LIMITS.customerSegment)
  ) {
    return `Keep each product and customer segment to ${LIMITS.product} characters.`;
  }
  if (company.operatingPriorities.some((item) => item.length > LIMITS.operatingPriority)) {
    return `Keep each operating priority to ${LIMITS.operatingPriority} characters.`;
  }

  const enabled = draft.teams.filter((team) => team.enabled);
  if (enabled.length === 0) return "Keep at least one Team enabled.";
  if (enabled.length > MAX_ENABLED_TEAMS) return "You can enable up to five Teams during setup.";
  const normalizedEnabledNames = enabled.map((team) =>
    team.teamName.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-US"),
  );
  if (new Set(normalizedEnabledNames).size !== normalizedEnabledNames.length) {
    return "Give every enabled Team a distinct name.";
  }
  for (const team of draft.teams) {
    if (!team.teamName.trim()) return "Every Team needs a name.";
    if (team.teamName.trim().length > LIMITS.teamName) {
      return `Keep every Team name to ${LIMITS.teamName} characters.`;
    }
    const responsibilities = lines(team.responsibilities);
    if (responsibilities.length === 0) return `${team.teamName || "Each Team"} needs at least one Responsibility.`;
    if (responsibilities.length > LIMITS.maxResponsibilities) {
      return `${team.teamName} can have at most ${LIMITS.maxResponsibilities} Responsibilities here.`;
    }
    if (responsibilities.some((item) => item.length > LIMITS.responsibility)) {
      return `Keep each ${team.teamName} Responsibility to ${LIMITS.responsibility} characters.`;
    }
    const playbookRules = lines(team.playbookRules);
    if (playbookRules.length > LIMITS.maxPlaybookRules) {
      return `${team.teamName} can start with at most ${LIMITS.maxPlaybookRules} Playbook rules.`;
    }
    if (playbookRules.some((item) => item.length > LIMITS.playbookRule)) {
      return `Keep each ${team.teamName} Playbook suggestion to ${LIMITS.playbookRule} characters.`;
    }
  }
  return null;
}

export function confidenceVariant(
  confidence: ResearchConfidence,
): "success" | "warning" | "neutral" {
  if (confidence === "high") return "success";
  if (confidence === "medium") return "warning";
  return "neutral";
}

export function connectorLabel(id: string): string {
  const known = CONNECTION_BY_ID.get(id)?.label;
  if (known) return known;
  return id
    .replace(/^google_/, "Google ")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function cadenceLabel(kind: CadenceKind): string {
  switch (kind) {
    case "editions":
      return "Morning, midday, and evening";
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "as_it_happens":
      return "As it happens";
  }
}
